import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { RootState } from '../../store';
import {
  fetchBOMsStart,
  deleteBOMStart,
  openBOMDialog,
  setBOMSearchTerm,
  setBOMActiveFilter,
  setBOMPage,
  setBOMPageSize,
  clearBOMError,
  exportBOMStart,
  fetchDropdownsStart,
} from '../../store/reducers/bomSlice';
import { BOM } from '../../services/bomApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchField: {
    minWidth: 250,
  },
  filterField: {
    minWidth: 120,
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  statusChip: {
    fontWeight: 'bold',
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  summaryCard: {
    marginBottom: theme.spacing(3),
  },
  summaryItem: {
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

const BOMList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    boms,
    bomLoading,
    bomError,
    bomPagination,
    bomSearchTerm,
    bomActiveFilter,
    exportLoading,
    exportError,
  } = useSelector((state: RootState) => state.bom);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bomToDelete, setBomToDelete] = useState<BOM | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchBOMsStart());
    dispatch(fetchDropdownsStart());
  }, [dispatch]);

  useEffect(() => {
    if (bomError || exportError) {
      setSnackbarOpen(true);
    }
  }, [bomError, exportError]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setBOMSearchTerm(event.target.value));
  };

  const handleActiveFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    dispatch(setBOMActiveFilter(value === '' ? null : value === 'true'));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setBOMPage(newPage + 1));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setBOMPageSize(parseInt(event.target.value, 10)));
  };

  const handleCreateBOM = () => {
    dispatch(openBOMDialog({ mode: 'create' }));
  };

  const handleEditBOM = (bom: BOM) => {
    dispatch(openBOMDialog({ mode: 'edit', bom }));
  };

  const handleDeleteBOM = (bom: BOM) => {
    setBomToDelete(bom);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (bomToDelete?.id) {
      dispatch(deleteBOMStart(bomToDelete.id));
    }
    setDeleteDialogOpen(false);
    setBomToDelete(null);
  };

  const handleViewBOM = (bom: BOM) => {
    navigate(`/bom/${bom.id}`);
  };

  const handleRefresh = () => {
    dispatch(fetchBOMsStart());
  };

  const handleExport = () => {
    dispatch(exportBOMStart());
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    dispatch(clearBOMError());
  };

  const getStatusChip = (active: boolean) => (
    <Chip
      label={active ? 'Active' : 'Inactive'}
      size="small"
      className={`${classes.statusChip} ${active ? classes.activeChip : classes.inactiveChip}`}
    />
  );

  const getSummaryStats = () => {
    const totalBOMs = boms.length;
    const activeBOMs = boms.filter(bom => bom.Active).length;
    const inactiveBOMs = totalBOMs - activeBOMs;
    
    return { totalBOMs, activeBOMs, inactiveBOMs };
  };

  const stats = getSummaryStats();

  if (bomLoading && boms.length === 0) {
    return (
      <Box className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
        </div>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Bill of Materials (BOM)
        </Typography>
        <div>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={bomLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton onClick={handleExport} disabled={exportLoading}>
              <ExportIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateBOM}
            className={classes.actionButton}
          >
            Add New BOM
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <Card className={classes.summaryCard}>
        <CardContent>
          <Box display="flex" justifyContent="space-around">
            <div className={classes.summaryItem}>
              <Typography className={classes.summaryValue}>
                {stats.totalBOMs}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Total BOMs
              </Typography>
            </div>
            <div className={classes.summaryItem}>
              <Typography className={classes.summaryValue} style={{ color: '#4caf50' }}>
                {stats.activeBOMs}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Active BOMs
              </Typography>
            </div>
            <div className={classes.summaryItem}>
              <Typography className={classes.summaryValue} style={{ color: '#f44336' }}>
                {stats.inactiveBOMs}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Inactive BOMs
              </Typography>
            </div>
          </Box>
        </CardContent>
      </Card>

      {/* Search and Filter Controls */}
      <div className={classes.controls}>
        <TextField
          label="Search BOMs"
          variant="outlined"
          size="small"
          value={bomSearchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <SearchIcon />,
          }}
          className={classes.searchField}
        />

        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filters
        </Button>

        {showFilters && (
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Status</InputLabel>
            <Select
              value={bomActiveFilter === null ? '' : bomActiveFilter.toString()}
              onChange={handleActiveFilterChange}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        )}

        {bomLoading && <CircularProgress size={24} />}
      </div>

      {/* BOM Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>BOM Name</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Component</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className={classes.noData}>
                  {bomSearchTerm || bomActiveFilter !== null 
                    ? 'No BOMs found matching your search criteria.' 
                    : 'No BOMs found. Click "Add New BOM" to create your first bill of materials.'}
                </TableCell>
              </TableRow>
            ) : (
              boms.map((bom) => (
                <TableRow key={bom.id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {bom.Name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bom.Product?.Name || 'Unknown Product'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bom.Component?.Name || 'Unknown Component'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(bom.Active)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bom.CreatedDate 
                        ? new Date(bom.CreatedDate).toLocaleDateString()
                        : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewBOM(bom)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit BOM">
                      <IconButton size="small" onClick={() => handleEditBOM(bom)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete BOM">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteBOM(bom)}
                        style={{ color: '#f44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={bomPagination.totalCount}
        rowsPerPage={bomPagination.pageSize}
        page={bomPagination.currentPage - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the BOM "{bomToDelete?.Name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={bomError || exportError}
      />
    </Box>
  );
};

export default BOMList; 